import React from 'react';
import { Check, X, HelpCircle } from 'lucide-react';
import { COMPARISON_DATA, CLIMATE_PLANS, Plan, ComparisonRow } from '../lib/plans/climate-data';

interface Props {
  plans?: Plan[];
  comparisonData?: ComparisonRow[];
  renderPlanHeaderExtra?: (plan: Plan) => React.ReactNode;
  renderPlanFooterExtra?: (plan: Plan) => React.ReactNode;
}

export default function PlanComparisonTable({
  plans = CLIMATE_PLANS,
  comparisonData = COMPARISON_DATA,
  renderPlanHeaderExtra,
  renderPlanFooterExtra,
}: Props) {
  const colSpan = 1 + plans.length;

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left" style={{ tableLayout: 'fixed' }}>
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="py-4 px-6 font-semibold text-gray-900">Features</th>
              {plans.map((plan, idx) => (
                <th
                  key={plan.id}
                  className={`py-4 px-6 font-bold text-center ${
                    plan.code === 'optimum'
                      ? 'bg-amber-50'
                      : plan.code === 'premier'
                        ? 'bg-blue-50'
                        : 'bg-white'
                  }`}
                >
                  {plan.popular && (
                    <div className="mx-auto mb-2 inline-flex items-center rounded-full bg-blue-600 text-white px-3 py-1 text-xs font-extrabold tracking-wide shadow">
                      Most Popular
                    </div>
                  )}
                  <div className="text-lg" style={{ color: plan.color }}>
                    {plan.name}
                  </div>
                  {renderPlanHeaderExtra && <div className="mt-3">{renderPlanHeaderExtra(plan)}</div>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {comparisonData.map((row, idx) => {
              if (row.isSection) {
                return (
                  <tr key={`section-${idx}`} className="bg-gray-50">
                    <td
                      colSpan={colSpan}
                      className="py-3 px-6 text-xs font-bold tracking-wider text-gray-700 uppercase"
                    >
                      {row.feature}
                    </td>
                  </tr>
                );
              }

              return (
                <tr
                  key={idx}
                  className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/60'} hover:bg-gray-100 transition-colors`}
                >
                  <td className="py-4 px-6 font-medium text-gray-900 flex items-center">
                    {row.feature}
                    {row.tooltip && (
                      <div className="group relative ml-2">
                        <HelpCircle className="w-4 h-4 text-gray-400 cursor-help" />
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 p-2 bg-gray-900 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                          {row.tooltip}
                          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
                        </div>
                      </div>
                    )}
                  </td>

                  {plans.map((plan) => {
                    const key = plan.code as keyof typeof row;
                    const val = row[key];
                    const cellBg =
                      plan.code === 'optimum'
                        ? 'bg-amber-50/40'
                        : plan.code === 'premier'
                          ? 'bg-blue-50/40'
                          : '';

                    return (
                      <td key={plan.id} className={`py-4 px-6 text-center ${cellBg}`}>
                        {val === true && <Check className="w-5 h-5 text-green-600 mx-auto" />}
                        {val === false && <X className="w-5 h-5 text-gray-300 mx-auto" />}
                        {typeof val === 'string' && <span className="font-medium text-gray-700">{val}</span>}
                        {(val === undefined || val === null) && (
                          <span className="font-medium text-gray-400">—</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}

            {renderPlanFooterExtra && (
              <tr className="bg-gray-50">
                <td className="py-4 px-6 font-semibold text-gray-900">Ready to pick a plan?</td>
                {plans.map((plan) => {
                  const cellBg =
                    plan.code === 'optimum'
                      ? 'bg-amber-50/40'
                      : plan.code === 'premier'
                        ? 'bg-blue-50/40'
                        : '';

                  return (
                    <td key={plan.id} className={`py-4 px-6 text-center ${cellBg}`}>
                      {renderPlanFooterExtra(plan)}
                    </td>
                  );
                })}
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
